import React, { useState } from 'react';
import ChatSendIcon from 'components/atoms/icons/chatSendIcon';
import BaseButton from 'components/atoms/baseButton';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import EmptyNoteIcon from 'components/atoms/icons/emptyNoteIcon';
import { usePatientNotes } from 'infrastructure/hooks/patient/use-patient-notes';
import { usePatientNotesCreate } from 'infrastructure/hooks/patient/use-patient-notes-create';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import BaseTextArea from 'components/atoms/base-textarea';

import s from './styles.module.scss';

import type { RootState } from 'store';

type Note = {
  guid: string;
  note: string;
  patientGuid: string;
  createdAt: string;
  createdBy: string;
  author: string;
};

const InternalNotes = () => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>('');

  const { currentConversation } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const { patientNotes, patientNotesLoading } = usePatientNotes({
    patientGuid: currentConversation?.patient.patientGuid,
  });

  const { createNote, createLoading } = usePatientNotesCreate({
    onSuccess() {
      setValue('');
    },
  });

  const onSubmit = async () => {
    createNote({
      patientGuid: currentConversation?.patient.patientGuid,
      note: value,
    });
  };

  const formatDate = (date: string) =>
    DateFormatter({
      date,
      format: DateFormats['MM-dd-yyyy | hh:mm a'],
    });

  return (
    <>
      <div className={s['chat-profile']}>
        <div className={s['notes-wrapper']}>
          <div className={s.notes}>
            {!patientNotesLoading ? (
              <>
                {patientNotes.length > 0 &&
                  patientNotes.map((note: Note) => (
                    <div className={s.note} key={note.guid}>
                      <div className={s['note-header']}>
                        <div className={s['note-date']}>
                          <span>{formatDate(note.createdAt)}</span>
                        </div>
                      </div>
                      <div className={s['note-body']}>{note.note}</div>
                      <div className={s['note-author']}>
                        <span>{note.author}</span>
                      </div>
                    </div>
                  ))}
                {patientNotes.length === 0 && (
                  <div className={s.empty}>
                    <EmptyNoteIcon />
                    <p>{t('smsDashboard.empty_notes')}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={s.note}>
                  <div className={s['note-body']}>
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                  </div>
                  <div className={s['note-date']}>
                    <Skeleton width={140} height={18} />
                  </div>
                </div>
                <div className={s.note}>
                  <div className={s['note-body']}>
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                  </div>
                  <div className={s['note-date']}>
                    <Skeleton width={140} height={18} />
                  </div>
                </div>
                <div className={s.note}>
                  <div className={s['note-body']}>
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                  </div>
                  <div className={s['note-date']}>
                    <Skeleton width={140} height={18} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className={`${s['messages-input-wrapper']} ${s['note-input']}`}>
        <div className={s['chat-form-wrapper']}>
          <div className={s['input-wrapper']}>
            <BaseTextArea
              value={value}
              placeholder={t('smsDashboard.type_note')}
              onChange={(message: string) => setValue(message)}
              disabled={patientNotesLoading || createLoading}
              rows={1.03}
              maxRows={6.9}
              plaintext={false}
              style={{
                padding: '8.5px 57px 8.5px 12px',
                lineHeight: '15.6px',
                maxHeight: '200px',
              }}
              onKeyDown={(e) => {
                if (
                  (e.key === 'Enter' || e.key === 'Return') &&
                  (e.ctrlKey || e.metaKey)
                ) {
                  e.preventDefault();
                  if (!value.trim() || patientNotesLoading || createLoading) {
                    return;
                  }
                  onSubmit();
                }
              }}
              autoFocus
            />
            <div className={s['send-button']}>
              <BaseButton
                type="secondary"
                disabled={value === ''}
                loading={patientNotesLoading || createLoading}
                onClick={onSubmit}
                icon={<ChatSendIcon />}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InternalNotes;
